import React, {useState, useEffect, useRef, useCallback} from 'react';
import {getForms, updateFormOther, deleteForm, getNotifierList, saveNotifier} from './api/forms';
import {API_CONFIG} from './config/api';
import toastManager from './utils/toastManager';
import { handleApiError } from './utils/errorHandler';
import {QRCodeCanvas} from 'qrcode.react';
import {useNavigate, useLocation} from 'react-router-dom';
import TopNavbar from './components/TopNavbar';
import './components/TopNavbar.css';
import {isWechatBrowser} from './config/wechat';
import { buildAuthRedirectUrl, executeAuthRedirect } from './utils/authRedirectManager';
import FormFiller from './FormFiller';
import { checkEditPermission } from './utils/permissionUtils';


// 添加CSS动画样式
const fadeInUpAnimation = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(-8px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

// 将样式注入到页面
if (!document.getElementById('form-list-animations')) {
    const style = document.createElement('style');
    style.id = 'form-list-animations';
    style.textContent = fadeInUpAnimation;
    document.head.appendChild(style);
}

export default function FormList({onCreateForm}) {
    const [search, setSearch] = useState('');
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [menuOpenId, setMenuOpenId] = useState(null);
    const [labelEditId, setLabelEditId] = useState(null);
    const [labelInput, setLabelInput] = useState('');
    const [publishModal, setPublishModal] = useState({open: false, type: '', url: '', qrcode: ''});
    const [showAllForms, setShowAllForms] = useState(false);
    const [notifierModal, setNotifierModal] = useState({open: false, data: null, loading: false, formId: null});
    const [notifierCheckboxes, setNotifierCheckboxes] = useState({});
    const [previewModal, setPreviewModal] = useState({open: false, formData: null});
    const isRequestingRef = useRef(false);
    const currentRequestRef = useRef(null);
    const [pendingSearch, setPendingSearch] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // 检查用户权限
    const hasEditAllPermission = checkEditPermission();

    // 从URL参数读取状态
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        const pageParam = searchParams.get('page');
        
        if (tab === 'personal') {
            setShowAllForms(false);
        } else if (tab === 'all') {
            setShowAllForms(true);
        }
        
        if (pageParam) {
            const pageNum = parseInt(pageParam);
            if (pageNum > 0) {
                setPage(pageNum);
            }
        }
    }, [location.search]);

    // 检查微信授权和跳转逻辑
    useEffect(() => {
        const checkWechatAuth = () => {
            console.log('🔍 FormList: 检查微信授权和跳转逻辑');
            console.log('📍 当前页面:', window.location.pathname);
            console.log('📱 是否微信环境:', isWechatBrowser());

            // 检查是否在微信浏览器中
            if (!isWechatBrowser()) {
                console.log('🔍 非微信环境，跳过授权检查');
                return;
            }

            // 检查是否已经有微信用户信息（已授权成功）
            const wechatUserStr = localStorage.getItem('wechatUser');
            if (wechatUserStr) {
                try {
                    const wechatUser = JSON.parse(wechatUserStr);
                    if (wechatUser && wechatUser.openid) {
                        console.log('✅ FormList: 检测到已授权用户，跳过所有授权检查和跳转');
                        return;
                    }
                } catch (error) {
                    console.log('⚠️ 解析wechatUser失败，继续检查授权逻辑');
                }
            }

            // 移除微信授权跳转逻辑，避免循环
            console.log('🔍 FormList: 已移除微信授权跳转逻辑');
        };

        // 立即执行检查
        checkWechatAuth();
    }, []);

    // 使用useCallback包装点击处理函数
    const handleNotifierClick = useCallback(async (formId) => {
        if (isRequestingRef.current) {
            console.log('正在请求中，忽略重复点击');
            return;
        }

        const requestKey = `${formId}_${Date.now()}`;
        console.log('点击反馈提醒按钮，formId:', formId, '请求ID:', requestKey);

        isRequestingRef.current = true;
        currentRequestRef.current = requestKey;
        setNotifierModal({open: true, data: null, loading: true, formId: formId});

        try {
            console.log('开始调用getNotifierList接口...', requestKey);
            const response = await getNotifierList(formId);

            // 检查请求是否仍然有效
            if (currentRequestRef.current !== requestKey) {
                console.log('请求已过期，忽略响应', requestKey);
                return;
            }

            console.log('反馈提醒数据:', response);
            console.log('响应类型:', typeof response);
            console.log('是否为数组:', Array.isArray(response));

            // 检查响应格式，支持多种返回格式
            let data = null;
            if (response && response.code === 200 && response.data) {
                // 格式: { code: 200, data: [...] }
                data = response.data;
            } else if (Array.isArray(response)) {
                // 格式: [...] (直接返回数组)
                data = response;
            } else if (response && Array.isArray(response.data)) {
                // 格式: { data: [...] }
                data = response.data;
            } else if (response && response.data) {
                // 其他格式
                data = response.data;
            }

            if (data) {
                // 初始化复选框状态
                const initialCheckboxes = {};
                if (Array.isArray(data)) {
                    data.forEach(item => {
                        initialCheckboxes[item.userId] = item.isCheck === "1";
                    });
                }
                console.log('初始化的复选框状态:', initialCheckboxes);
                setNotifierCheckboxes(initialCheckboxes);
                setNotifierModal({open: true, data: data, loading: false, formId: formId});
                isRequestingRef.current = false;
                currentRequestRef.current = null;
            } else {
                console.error('响应数据格式不正确:', response);
                toastManager.error('数据格式错误');
                setNotifierModal({open: false, data: null, loading: false, formId: null});
                isRequestingRef.current = false;
                currentRequestRef.current = null;
            }
        } catch (error) {
            console.error('获取反馈提醒数据失败:', error);
            console.error('错误详情:', {
                message: error.message, stack: error.stack, formId: formId
            });
            toastManager.error(`获取数据失败: ${error.message}`);
            setNotifierModal({open: false, data: null, loading: false, formId: null});
            isRequestingRef.current = false;
            currentRequestRef.current = null;
        }
    }, []);


    const menuBtnRefs = useRef({});
    const menuRefs = useRef({});
    // 在组件内定义 ref
    // eslint-disable-next-line no-unused-vars
    const qrRef = useRef(null);
    // 新增：页面加载时校验token，无token则跳转到登录页
    useEffect(() => {
        const token = localStorage.getItem('token');
        const fromWechatForm = localStorage.getItem('fromWechatForm');
        const isProcessingWechatAuth = localStorage.getItem('isProcessingWechatAuth');
        
        if (!token) {
            // 检查是否在微信环境中
            const isWechat = /MicroMessenger/i.test(navigator.userAgent);
            
            if (fromWechatForm === '1') {
                console.log('📱 进入 fromWechatForm 分支');
                // navigate('/wechat-guide', { replace: true });
            } else if (isProcessingWechatAuth && isProcessingWechatAuth !== 'null' && isProcessingWechatAuth !== '') {
                console.log('🔄 进入 isProcessingWechatAuth 分支');
                
                // 使用统一的跳转管理器
                const rawReturnUrl = localStorage.getItem("returnUrl");
                const finalUrl = buildAuthRedirectUrl('wechat_auth');
                
                console.log('📋 跳转信息:', { rawReturnUrl, finalUrl });

                // 在跳转前清除标记，避免重复处理
                localStorage.removeItem("isProcessingWechatAuth");
                
                // 执行跳转
                executeAuthRedirect(finalUrl, { method: 'navigate', navigate });
            } else if (isWechat) {
                console.log('📱 微信环境中无token，跳转到微信授权');
                // 保存当前路径作为授权后的跳转目标
                localStorage.setItem('returnUrl', window.location.href);
                // window.location.href = 'https://form.fangxiaojia.vip/microform-test/wechat-auth'; // 临时测试域名
                window.location.href = `${window.location.origin}/wechat-auth`; // 动态获取当前域名
            } else {
                console.log('🔐 非微信环境，跳转到登录页');
                // navigate('/login', {replace: true}); // 注释掉跳转到登录页
            }
        }
    }, []); // 移除 navigate 依赖，避免重复执行


    useEffect(() => {
        const fetchForms = async () => {
            setLoading(true);
            try {
                const params = {page, limit, search};
                params.allView = showAllForms ? 'true' : '';
                const res = await getForms(params);
                // 统一使用 list 字段，兼容 content 字段
                const formsArr = Array.isArray(res.data?.list) ? res.data.list : Array.isArray(res.data?.content) ? res.data.content : [];

                const mappedForms = formsArr.map(item => ({
                    id: item.id,
                    formId: item.formId,
                    title: item.name,
                    tag: item.label || '无标签',
                    department: item.department || '', // 部门
                    top: item.isTop === "1",
                    preview: true,
                    views: item.viewCount ?? 0,
                    feedback: item.feedBackCount ?? 0,
                    people: 0,
                    status: item.isClose === "1" ? 'inactive' : 'active',
                    formSchema: item.formSchema,
                    qrCode: item.qrCode,
                    pathUrl: item.pathUrl,
                    formUrl: item.formUrl,
                    notificationNum: item.notificationNum ?? 0,
                }));

                setForms(mappedForms);
                setTotal(res.data?.total || 1);
                setPage(res.data?.page || 1);
            } catch (e) {
                console.error('获取表单列表失败:', e);
                
                // 使用新的错误处理工具
                const result = handleApiError(e, null, true);
                if (result.shouldRedirect) {
                    // navigate('/login', { replace: true }); // 注释掉跳转到登录页
                    return;
                }
                
                setForms([]);
                setTotal(1);
            }
            setLoading(false);
        };
        fetchForms();
    }, [page, limit, search, showAllForms]);


    useEffect(() => {
        if (menuOpenId) {
            const handleClickOutside = (e) => {
                const btn = menuBtnRefs.current[menuOpenId];
                const menu = menuRefs.current[menuOpenId];
                if (btn && btn.contains(e.target)) return;
                if (menu && menu.contains(e.target)) return;
                setMenuOpenId(null);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [menuOpenId]);

    // useEffect 中，彻底移除 QRCodeCanvas.toDataURL、setQrCodeImg、setQrCodeLoading 相关逻辑

    // 搜索时重置到第一页
    const handleSearchChange = (e) => {
        setPendingSearch(e.target.value);
    };

    const handleSearch = () => {
        setSearch(pendingSearch);
        setPage(1);
    };

    const tagColor = {
        '公益验房': '#52c41a', '实木聚会': '#faad14', '欧铂丽': '#1890ff',
    };

    // 通用数据刷新函数
    const refreshFormsData = async () => {
        try {
            const params = {page, limit, search};
            params.allView = showAllForms ? 'true' : '';
            const res = await getForms(params);
            // 统一使用 list 字段，兼容 content 字段
            const formsArr = Array.isArray(res.data?.list) ? res.data.list : Array.isArray(res.data?.content) ? res.data.content : [];

            const mappedForms = formsArr.map(item => ({
                id: item.id,
                formId: item.formId,
                title: item.name,
                tag: item.label || '无标签',
                department: item.department || '', // 部门
                top: item.isTop === "1",
                preview: true,
                views: item.viewCount ?? 0,
                feedback: item.feedBackCount ?? 0,
                people: 0,
                status: item.isClose === "1" ? 'inactive' : 'active',
                formSchema: item.formSchema,
                qrCode: item.qrCode,
                pathUrl: item.pathUrl,
                formUrl: item.formUrl,
                notificationNum: item.notificationNum ?? 0,
            }));

            setForms(mappedForms);
            setTotal(res.data?.total || 1);
        } catch (error) {
            console.error('刷新表单数据失败:', error);
            toastManager.error('数据刷新失败');
        }
    };


    // 表单开启
    const handleOpen = async (id) => {
        try {
            await updateFormOther({id, isClose: "0"});
            setMenuOpenId(null);
            // 重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('开启表单失败:', error);
            toastManager.error('操作失败，请重试');
        }
    };

    // 关闭表单
    const handleClose = async (id) => {
        try {
            await updateFormOther({id, isClose: "1"});
            setMenuOpenId(null);
            // 重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('关闭表单失败:', error);
            toastManager.error('操作失败，请重试');
        }
    };
    // 删除
    const handleDelete = async (id) => {
        // 添加确认对话框
        if (!window.confirm('确定要删除这个表单吗？删除后无法恢复。')) {
            return;
        }

        try {
            await deleteForm(id);
            setMenuOpenId(null);
            // 使用与useEffect相同的逻辑重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('删除表单失败:', error);
            toastManager.error('删除失败，请重试');
        }
    };
    // 置顶
    const handleTop = async (id) => {
        try {
            await updateFormOther({id, isTop: "1"});
            setMenuOpenId(null);
            // 重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('置顶表单失败:', error);
            toastManager.error('操作失败，请重试');
        }
    };

    // 取消置顶
    const handleUnTop = async (id) => {
        try {
            await updateFormOther({id, isTop: "0"});
            setMenuOpenId(null);
            // 重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('取消置顶失败:', error);
            toastManager.error('操作失败，请重试');
        }
    };

    // 更改标签
    const handleChangeLabel = async (id) => {
        if (!labelInput.trim()) {
            toastManager.error('标签不能为空');
            return;
        }
        try {
            await updateFormOther({id, label: labelInput.trim()});
            setLabelEditId(null);
            setLabelInput('');
            setMenuOpenId(null);
            // 重新获取数据
            await refreshFormsData();
        } catch (error) {
            console.error('更新标签失败:', error);
            toastManager.error('操作失败，请重试');
        }
    };

    // 预览表单
    const handlePreviewForm = (form) => {
        try {
            // 解析表单schema
            const formSchema = JSON.parse(form.formSchema || '{}');
            setPreviewModal({
                open: true,
                formData: {
                    title: formSchema.title || form.title,
                    formDesc: formSchema.formDesc || '',
                    components: formSchema.components || [],
                    showTitle: formSchema.showTitle !== false
                }
            });
        } catch (error) {
            console.error('解析表单schema失败:', error);
            // 如果解析失败，使用基本信息
            setPreviewModal({
                open: true,
                formData: {
                    title: form.title,
                    formDesc: '',
                    components: []
                }
            });
        }
    };

    // 复制表单
    const handleCopy = async (id) => {
        try {
            console.log('🔄 开始复制表单，ID:', id);
            const res = await fetch(`${API_CONFIG.BASE_URL}/forms/copy_form?id=${id}`, {
                method: 'GET', headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
            });
            const result = await res.json();
            console.log('📋 复制表单响应:', result);
            
            if (result.code === 200) {
                // 显示成功提示
                toastManager.success('表单复制成功！');
                console.log('✅ 表单复制成功');
                
                // 重新获取数据
                await refreshFormsData();
            } else {
                console.error('❌ 复制表单失败:', result.message);
                toastManager.error(result.message || '复制失败');
            }
        } catch (e) {
            console.error('❌ 复制表单异常:', e);
            toastManager.error('复制失败，请稍后重试');
        }
        setMenuOpenId(null);
    };

    // 抽奖功能 - 跳转到抽奖页面
    const handleLottery = (formId) => {
        navigate(`/lottery/${formId}`);
    };

    return (<>
        <TopNavbar/>
        <style>
            {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes bounce {
            0%, 20%, 53%, 80%, 100% {
              transform: translate3d(0,0,0);
            }
            40%, 43% {
              transform: translate3d(0, -15px, 0);
            }
            70% {
              transform: translate3d(0, -7px, 0);
            }
            90% {
              transform: translate3d(0, -2px, 0);
            }
          }
        `}
        </style>
        <div style={{background: '#f4f6fa', minHeight: '100vh', padding: '40px 0'}}>
            <div style={{maxWidth: 900, margin: '0 auto'}}>
                <div style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px',
                    marginBottom: '32px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.04)'
                }}>
                    {/* 搜索和操作栏 */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap'
                    }}>
                        <div style={{
                            flex: '1', minWidth: '300px', position: 'relative'
                        }}>
                            <input
                                value={pendingSearch}
                                onChange={handleSearchChange}
                                placeholder=" 输入表单名称来搜索"
                                style={{
                                    width: '100%',
                                    padding: '14px 16px 14px 44px',
                                    borderRadius: '12px',
                                    border: '2px solid #e8eaed',
                                    fontSize: '16px',
                                    background: '#fafbfc',
                                    transition: 'all 0.3s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#667eea';
                                    e.target.style.background = '#fff';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e8eaed';
                                    e.target.style.background = '#fafbfc';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                left: '16px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#999',
                                fontSize: '16px'
                            }}>
                                🔍
                            </div>
                        </div>

                        <button style={{
                            padding: '14px 24px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            minWidth: '100px'
                        }}
                                onClick={handleSearch}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                                }}
                        >
                            搜索
                        </button>

                        <button
                            style={{
                                padding: '14px 24px',
                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.3)',
                                transition: 'all 0.3s ease',
                                minWidth: '120px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                            onClick={() => navigate('/forms/edit/new')}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 20px rgba(82, 196, 26, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 16px rgba(82, 196, 26, 0.3)';
                            }}
                        >
                            <span style={{fontSize: '18px'}}>+</span>
                            创建表单
                        </button>
                    </div>

                    {/* 视图切换栏 */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '20px 0',
                        borderTop: '1px solid #f0f0f0',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                <span style={{
                    fontSize: '15px', color: '#666', fontWeight: '500'
                }}>
                  视图模式:
                </span>
                            <div style={{
                                display: 'inline-flex',
                                background: '#f5f7fa',
                                borderRadius: '12px',
                                padding: '4px',
                                border: '1px solid #e8eaed'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowAllForms(false);
                                        setPage(1);
                                        navigate('/forms?tab=personal&page=1', { replace: true });
                                    }}
                                    style={{
                                        background: !showAllForms ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                        color: !showAllForms ? '#fff' : '#666',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        minWidth: '80px',
                                        boxShadow: !showAllForms ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (showAllForms) {
                                            e.target.style.background = '#f0f0f0';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (showAllForms) {
                                            e.target.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    我的表单
                                </button>
                                <button
                                    onClick={() => {
                                        setShowAllForms(true);
                                        setPage(1);
                                        navigate('/forms?tab=all&page=1', { replace: true });
                                    }}
                                    style={{
                                        background: showAllForms ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                        color: showAllForms ? '#fff' : '#666',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        transition: 'all 0.3s ease',
                                        minWidth: '80px',
                                        boxShadow: showAllForms ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!showAllForms) {
                                            e.target.style.background = '#f0f0f0';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!showAllForms) {
                                            e.target.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    全部表单
                                </button>
                            </div>
                        </div>

                        <div style={{
                            fontSize: '14px',
                            color: '#666',
                            background: showAllForms ? '#fff7e6' : '#f6ffed',
                            border: `1px solid ${showAllForms ? '#ffd591' : '#b7eb8f'}`,
                            borderRadius: '8px',
                            padding: '8px 16px',
                            fontWeight: '500'
                        }}>
                            {showAllForms ? 
                                (hasEditAllPermission ? '👁️ 查看所有用户的表单' : '👁️ 查看所有用户的表单（只读模式）') 
                                : '✏️ 仅查看我的表单（可编辑）'}
                        </div>
                    </div>
                </div>


                {loading ? (<div style={{
                    textAlign: 'center',
                    color: '#666',
                    padding: '60px 20px',
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid rgba(0,0,0,0.04)'
                }}>
                    <div style={{
                        display: 'inline-block',
                        width: '40px',
                        height: '40px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #667eea',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '16px'
                    }}></div>
                    <div style={{fontSize: '16px', fontWeight: '500'}}>加载中...</div>
                    <div style={{fontSize: '14px', color: '#999', marginTop: '8px'}}>正在获取表单数据</div>
                </div>) : (<>
                    {forms.map(form => (<div key={form.id} style={{
                        background: '#fff',
                        borderRadius: '16px',
                        marginBottom: '24px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        padding: '28px',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        borderLeft: form.top ? '4px solid #ff7875' : '4px solid #f4f6fa',
                        cursor: showAllForms ? 'default' : 'pointer',
                        border: '1px solid rgba(0,0,0,0.04)',
                        overflow: 'hidden'
                    }}
                                             onClick={() => {
                                                 // 不做任何操作
                                             }}
                                             onMouseEnter={(e) => {
                                                 if (!showAllForms) {
                                                     e.target.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)';
                                                     e.target.style.transform = 'translateY(-2px)';
                                                 }
                                             }}
                                             onMouseLeave={(e) => {
                                                 if (!showAllForms) {
                                                     e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                                     e.target.style.transform = 'translateY(0)';
                                                 }
                                             }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '16px',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 8px',
                                background: form.status === 'active' ? '#f6ffed' : '#f5f5f5',
                                borderRadius: '8px',
                                border: `1px solid ${form.status === 'active' ? '#b7eb8f' : '#d9d9d9'}`
                            }}>
                    <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: form.status === 'active' ? '#52c41a' : '#ccc',
                        display: 'inline-block'
                    }}></span>
                                <span style={{
                                    fontSize: '12px',
                                    color: form.status === 'active' ? '#52c41a' : '#666',
                                    fontWeight: '500'
                                }}>
                        {form.status === 'active' ? '活跃' : '已关闭'}
                      </span>
                            </div>

                            <span style={{
                                fontWeight: '700',
                                fontSize: '22px',
                                color: '#1a1a1a',
                                flex: '1',
                                lineHeight: '1.3',
                                minWidth: '200px'
                            }}>
                      {form.title}
                    </span>

                            <button
                                style={{
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '6px 14px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                                    transition: 'all 0.2s ease',
                                    marginRight: '8px',
                                    lineHeight: '1.2',
                                    height: '27.59px',
                                    minWidth: '60px',
                                    boxSizing: 'border-box'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePreviewForm(form);
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 8px rgba(24, 144, 255, 0.3)';
                                }}
                            >
                                预览
                            </button>

                            <button
                                style={{
                                    color: '#ff6b35',
                                    fontWeight: '500',
                                    background: 'rgba(255, 107, 53, 0.08)',
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    border: '1px solid rgba(255, 107, 53, 0.2)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    marginRight: '8px',
                                    lineHeight: '1.2',
                                    height: '27.59px',
                                    minWidth: '60px',
                                    boxSizing: 'border-box'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleLottery(form.formId);
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'rgba(255, 107, 53, 0.15)';
                                    e.target.style.borderColor = 'rgba(255, 107, 53, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'rgba(255, 107, 53, 0.08)';
                                    e.target.style.borderColor = 'rgba(255, 107, 53, 0.2)';
                                }}
                            >
                                <span style={{fontSize: '11px'}}>
                                    🎯
                                </span>
                                抽奖
                            </button>

                            <span style={{
                                background: tagColor[form.tag] || '#f0f0f0',
                                color: tagColor[form.tag] ? '#fff' : '#666',
                                borderRadius: '20px',
                                padding: '6px 14px',
                                fontSize: '13px',
                                fontWeight: '600',
                                display: 'inline-block',
                                boxShadow: tagColor[form.tag] ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                                lineHeight: '1.2',
                                height: '27.59px',
                                minWidth: '54px',
                                boxSizing: 'border-box'
                            }}>
                      {form.tag}
                    </span>

                            {/* 部门标签 */}
                            {form.department && (
                                <span style={{
                                    background: '#722ed1',
                                    color: '#fff',
                                    borderRadius: '20px',
                                    padding: '6px 14px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'inline-block',
                                    boxShadow: '0 2px 8px rgba(114, 46, 209, 0.3)',
                                    lineHeight: '1.2',
                                    height: '27.59px',
                                    minWidth: '54px',
                                    boxSizing: 'border-box'
                                }}>
                                  🏢 {form.department}
                                </span>
                            )}

                            {form.top && (<span style={{
                                color: '#fff',
                                fontWeight: '600',
                                fontSize: '13px',
                                borderRadius: '20px',
                                padding: '6px 14px',
                                background: 'linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%)',
                                boxShadow: '0 2px 8px rgba(255, 120, 117, 0.3)',
                                display: 'inline-block',
                                lineHeight: '1.2',
                                height: '27.59px',
                                minWidth: '54px',
                                boxSizing: 'border-box'
                            }}>
                        置顶
                      </span>)}
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%)',
                            borderRadius: '10px',
                            padding: '12px 16px',
                            marginBottom: '16px',
                            border: '1px solid #e8eaed',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap'
                            }}>
                      <span style={{
                          color: '#555',
                          fontSize: '13px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                      }}>
                        📤 发布表单
                      </span>
                                <button
                                    style={{
                                        color: '#667eea',
                                        background: 'rgba(102, 126, 234, 0.08)',
                                        border: '1px solid rgba(102, 126, 234, 0.2)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const shareUrl = form.pathUrl || `/detail/${form.formId}`;
                                        setPublishModal({
                                            open: true, type: 'url', url: shareUrl, qrcode: form.qrCode
                                        });
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.12)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                        e.target.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.08)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <span style={{fontSize: '12px'}}>🔗</span>
                                    获取地址
                                </button>
                                <button
                                    style={{
                                        color: '#667eea',
                                        background: 'rgba(102, 126, 234, 0.08)',
                                        border: '1px solid rgba(102, 126, 234, 0.2)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        padding: '5px 10px',
                                        borderRadius: '6px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const shareUrl = form.pathUrl || `/detail/${form.formId}`;
                                        setPublishModal({
                                            open: true, type: 'qrcode', url: shareUrl, qrcode: form.qrCode
                                        });
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.12)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                        e.target.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(102, 126, 234, 0.08)';
                                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <span style={{fontSize: '12px'}}>📱</span>
                                    生成二维码
                                </button>
                            </div>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '16px',
                            gap: '12px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'
                            }}>
                                <button
                                    style={{
                                        color: '#52c41a',
                                        fontWeight: '500',
                                        background: 'rgba(82, 196, 26, 0.08)',
                                        borderRadius: '6px',
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        border: '1px solid rgba(82, 196, 26, 0.2)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleNotifierClick(form.formId);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'rgba(82, 196, 26, 0.15)';
                                        e.target.style.borderColor = 'rgba(82, 196, 26, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(82, 196, 26, 0.08)';
                                        e.target.style.borderColor = 'rgba(82, 196, 26, 0.2)';
                                    }}
                                >
                                    <span style={{fontSize: '11px'}}>📊</span>
                                    反馈提醒
                                </button>


                                <span style={{
                                    background: 'rgba(102, 126, 234, 0.08)',
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    color: '#667eea',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px'
                                }}>
                        <span style={{fontSize: '11px'}}>👥</span>
                        共{form.notificationNum || 0}人
                      </span>

                                <span style={{
                                    color: '#faad14',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    background: 'rgba(250, 173, 20, 0.08)',
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    fontWeight: '500',
                                    border: '1px solid rgba(250, 173, 20, 0.2)'
                                }}>
                        <span style={{fontSize: '11px'}}>📈</span>
                      浏览量 {form.views || 0}
                    </span>
                            </div>
                            <button
                                style={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 16px',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                                    transition: 'all 0.3s ease',
                                    gap: '6px'
                                }}
                                onClick={() => {
                                    const currentTab = showAllForms ? 'all' : 'personal';
                                    navigate(`/forms/${form.formId}/submissions?from=${showAllForms ? 'all' : 'personal'}&page=${page}&tab=${currentTab}`);
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                    <path d="M4 10h12M4 10a6 6 0 1112 0" stroke="#fff" strokeWidth="2"
                                          strokeLinecap="round"/>
                                </svg>
                                <span>{form.feedback || 0}</span>
                                <span>条反馈</span>
                            </button>

                            <div style={{
                                marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center'
                            }}>
                                {!showAllForms && (<>
                                    {hasEditAllPermission || !showAllForms ? (
                                        <button
                                            style={{
                                                color: '#fff',
                                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                                border: 'none',
                                                borderRadius: '12px',
                                                padding: '8px 20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.3)',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/forms/edit/${form.formId}`);
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = '0 6px 20px rgba(82, 196, 26, 0.4)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 4px 16px rgba(82, 196, 26, 0.3)';
                                            }}
                                        >
                                            ✏️ 编辑表单
                                        </button>
                                    ) : (
                                        <button
                                            style={{
                                                color: '#999',
                                                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '12px',
                                                padding: '8px 20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'not-allowed',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                            disabled
                                            title="无权限编辑他人表单"
                                        >
                                            🔒 无权限编辑
                                        </button>
                                    )}
                                    <button
                                        ref={el => menuBtnRefs.current[form.id] = el}
                                        style={{
                                            color: '#555',
                                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '10px',
                                            padding: '10px 18px',
                                            fontSize: '14px',
                                            cursor: 'pointer',
                                            fontWeight: '500',
                                            position: 'relative',
                                            transition: 'all 0.3s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                            minWidth: '80px',
                                            justifyContent: 'center'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpenId(form.id);
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)';
                                            e.target.style.borderColor = '#adb5bd';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
                                            e.target.style.borderColor = '#dee2e6';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                        }}
                                    >
                                        <span style={{fontSize: '16px', lineHeight: '1'}}>⋯</span>
                                        <span>更多</span>
                                    </button>
                                </>)}
                                {showAllForms && (<>
                                    <button
                                        style={{
                                            marginLeft: 8,
                                            padding: '4px 10px',
                                            background: '#f5f5f5',
                                            color: '#222',
                                            border: '1px solid #ccc',
                                            borderRadius: 6,
                                            fontSize: 14,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleCopy(form.id)}
                                    >
                                        复制表单
                                    </button>
                                    {hasEditAllPermission && (
                                        <button
                                            style={{
                                                color: '#fff',
                                                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                                border: 'none',
                                                borderRadius: '12px',
                                                padding: '8px 20px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.3)',
                                                transition: 'all 0.3s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                marginLeft: '8px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/forms/edit/${form.formId}`);
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'translateY(-1px)';
                                                e.target.style.boxShadow = '0 6px 20px rgba(82, 196, 26, 0.4)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 4px 16px rgba(82, 196, 26, 0.3)';
                                            }}
                                        >
                                            ✏️ 编辑表单
                                        </button>
                                    )}
                                    <span style={{
                                        color: '#666',
                                        fontSize: '14px',
                                        padding: '8px 16px',
                                        background: '#f5f5f5',
                                        borderRadius: '12px',
                                        border: '1px solid #e0e0e0',
                                        fontWeight: '500'
                                    }}>
                                        {hasEditAllPermission ? '👁️ 可编辑模式' : '👁️ 只读模式'}
                                    </span>
                                </>)}
                            </div>

                        </div>
                    </div>))}

                    {/* 独立的下拉菜单 - 移到最外层 */}
                    {menuOpenId && forms.find(f => f.id === menuOpenId) && (() => {
                        const form = forms.find(f => f.id === menuOpenId);

                        // 获取按钮位置并计算菜单位置
                        const buttonElement = menuBtnRefs.current[form.id];
                        let menuStyle = {
                            background: '#fff',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e8eaed',
                            minWidth: '140px',
                            padding: '6px 0',
                            position: 'fixed',
                            zIndex: 10000,
                            animation: 'fadeInUp 0.2s ease-out',
                            overflow: 'hidden',
                            maxWidth: 'calc(100vw - 40px)',
                            left: '20px',
                            top: '20px'
                        };

                        // 如果按钮存在，计算正确位置
                        if (buttonElement) {
                            const buttonRect = buttonElement.getBoundingClientRect();
                            const viewportWidth = window.innerWidth;
                            const viewportHeight = window.innerHeight;
                            const menuWidth = 140;
                            const menuHeight = 200;

                            // 计算菜单位置 - 优先右对齐
                            let left = buttonRect.left;
                            let top = buttonRect.bottom + 8;

                            // 检查右边界，如果超出则左对齐
                            if (left + menuWidth > viewportWidth - 20) {
                                left = buttonRect.right - menuWidth;
                            }

                            // 检查下边界，如果超出则显示在按钮上方
                            if (top + menuHeight > viewportHeight - 20) {
                                top = buttonRect.top - menuHeight - 8;
                            }

                            // 确保不超出左边界
                            if (left < 20) {
                                left = 20;
                            }

                            // 确保不超出上边界
                            if (top < 20) {
                                top = 20;
                            }

                            menuStyle.left = `${left}px`;
                            menuStyle.top = `${top}px`;
                        }

                        return (<div
                            ref={el => menuRefs.current[form.id] = el}
                            style={menuStyle}
                        >
                            <div className="menu-item" style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                borderTop: 'none',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                display: 'block',
                                textAlign: 'left'
                            }}
                                 onMouseEnter={(e) => {
                                     e.target.style.background = '#f8f9fa';
                                 }}
                                 onMouseLeave={(e) => {
                                     e.target.style.background = 'transparent';
                                 }}
                                 onClick={() => handleCopy(form.id)}
                            >
                                复制表单
                            </div>
                            <div className="menu-item" style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                borderTop: 'none',
                                fontSize: '14px',
                                fontWeight: '500',
                                color: '#333',
                                display: 'block',
                                textAlign: 'left'
                            }}
                                 onMouseEnter={(e) => {
                                     e.target.style.background = '#f8f9fa';
                                 }}
                                 onMouseLeave={(e) => {
                                     e.target.style.background = 'transparent';
                                 }}
                                 onClick={() => {
                                     if (form.top) {
                                         handleUnTop(form.id);
                                     } else {
                                         handleTop(form.id);
                                     }
                                 }}
                            >
                                {form.top ? '取消置顶' : '置顶'}
                            </div>
                            <div className="menu-item" style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                borderTop: '1px solid #f0f0f0',
                                color: '#ff7875',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'block',
                                textAlign: 'left'
                            }}
                                 onMouseEnter={(e) => {
                                     e.target.style.background = '#fff2f0';
                                 }}
                                 onMouseLeave={(e) => {
                                     e.target.style.background = 'transparent';
                                 }}
                                 onClick={() => handleDelete(form.id)}
                            >
                                删除
                            </div>
                            <div className="menu-item" style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                borderTop: '1px solid #f0f0f0',
                                color: '#666',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'block',
                                textAlign: 'left'
                            }}
                                 onMouseEnter={(e) => {
                                     e.target.style.background = '#f8f9fa';
                                 }}
                                 onMouseLeave={(e) => {
                                     e.target.style.background = 'transparent';
                                 }}
                                 onClick={() => {
                                     if (form.status === 'inactive') {
                                         handleOpen(form.id);
                                     } else {
                                         handleClose(form.id);
                                     }
                                 }}
                            >
                                {form.status === 'inactive' ? '表单开启' : '关闭表单'}
                            </div>
                            <div className="menu-item" style={{
                                padding: '10px 16px',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                borderTop: '1px solid #f0f0f0',
                                color: '#1890ff',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'block',
                                textAlign: 'left'
                            }}
                                 onMouseEnter={(e) => {
                                     e.target.style.background = '#f0f8ff';
                                 }}
                                 onMouseLeave={(e) => {
                                     e.target.style.background = 'transparent';
                                 }}
                                 onClick={() => {
                                     setLabelEditId(form.id);
                                     setLabelInput(form.tag === '无标签' ? '' : form.tag);
                                 }}
                            >
                                更改标签
                            </div>
                            {labelEditId === form.id && (<div style={{
                                padding: '12px 20px',
                                borderTop: '1px solid #f0f0f0',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                background: '#f8f9fa',
                                borderRadius: '0 0 12px 12px'
                            }}>
                                <input
                                    value={labelInput}
                                    onChange={e => setLabelInput(e.target.value)}
                                    placeholder="输入新标签"
                                    style={{
                                        flex: 1,
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s ease'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#1890ff'}
                                    onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                />
                                <button
                                    onClick={() => handleChangeLabel(form.id)}
                                    style={{
                                        border: 'none',
                                        background: '#1890ff',
                                        color: '#fff',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'background 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = '#1765ad'}
                                    onMouseLeave={(e) => e.target.style.background = '#1890ff'}
                                >
                                    确定
                                </button>
                                <button
                                    onClick={() => setLabelEditId(null)}
                                    style={{
                                        border: '1px solid #dee2e6',
                                        background: '#fff',
                                        color: '#666',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = '#f8f9fa';
                                        e.target.style.borderColor = '#adb5bd';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = '#fff';
                                        e.target.style.borderColor = '#dee2e6';
                                    }}
                                >
                                    取消
                                </button>
                            </div>)}
                        </div>);
                    })()}
                    {/* 弹窗展示地址或二维码 */}
                    {publishModal.open && (<div style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.25)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: 16,
                            padding: 36,
                            minWidth: 340,
                            maxWidth: 420,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                            textAlign: 'center',
                            position: 'relative'
                        }}>
                            <button style={{
                                position: 'absolute',
                                right: 18,
                                top: 18,
                                background: 'none',
                                border: 'none',
                                fontSize: 24,
                                cursor: 'pointer',
                                color: '#222'
                            }} onClick={() => setPublishModal({
                                open: false, type: '', url: '', qrcode: ''
                            })}>×
                            </button>
                            {publishModal.type === 'url' ? (<>
                                <h2 style={{
                                    marginBottom: 24, fontWeight: 700, fontSize: 26, color: '#222'
                                }}>表单链接</h2>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 12
                                }}>
                                                    <span style={{
                                                        color: '#222',
                                                        fontSize: 16,
                                                        wordBreak: 'break-all',
                                                        background: '#f7f7f7',
                                                        padding: '6px 12px',
                                                        borderRadius: 6
                                                    }}>{publishModal.url}</span>
                                    <button style={{
                                        background: '#f5f5f5',
                                        color: '#222',
                                        border: '1px solid #ccc',
                                        borderRadius: 6,
                                        padding: '6px 18px',
                                        fontSize: 15,
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }} onClick={() => {
                                        navigator.clipboard.writeText(publishModal.url).then(() => console.log('复制成功')).catch(() => toastManager.error('复制失败，请手动复制'));
                                    }}>复制
                                    </button>
                                </div>
                            </>) : (<>
                                <h2 style={{
                                    marginBottom: 24, fontWeight: 700, fontSize: 26, color: '#222'
                                }}>表单二维码</h2>
                                <QRCodeCanvas value={publishModal.url} size={180} includeMargin={true}
                                              style={{
                                                  marginBottom: 8,
                                                  border: '1px solid #eee',
                                                  borderRadius: 12,
                                                  background: '#fafafa'
                                              }}/>
                                <div
                                    style={{fontSize: 14, color: '#666', marginBottom: 16}}>扫描二维码访问表单
                                </div>
                                <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                                    <button style={{
                                        background: '#1890ff',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '8px 16px',
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }} onClick={() => {
                                        const canvas = document.querySelector('canvas');
                                        const link = document.createElement('a');
                                        link.href = canvas.toDataURL('image/png');
                                        link.download = `form-qrcode-${Date.now()}.png`;
                                        link.click();
                                    }}>下载二维码
                                    </button>
                                    <button style={{
                                        background: '#52c41a',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '8px 16px',
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        fontWeight: 500
                                    }} onClick={() => {
                                        const testUrl = publishModal.url;
                                        window.open(testUrl, '_blank');
                                    }}>测试链接
                                    </button>
                                </div>
                            </>)}
                        </div>
                    </div>)}

                    {/* 反馈提醒弹窗 */}
                    {notifierModal.open && (<div style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.25)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <div style={{
                            background: '#fff',
                            borderRadius: 16,
                            padding: 36,
                            minWidth: 400,
                            maxWidth: '90vw',
                            maxHeight: '80vh',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                            position: 'relative',
                            overflow: 'auto'
                        }}>
                            <button
                                style={{
                                    position: 'absolute',
                                    right: 18,
                                    top: 18,
                                    background: 'none',
                                    border: 'none',
                                    fontSize: 24,
                                    cursor: 'pointer',
                                    color: '#222'
                                }}
                                onClick={() => {
                                    setNotifierModal({
                                        open: false, data: null, loading: false, formId: null
                                    });
                                    setNotifierCheckboxes({});
                                }}
                            >
                                ×
                            </button>

                            <h2 style={{
                                marginBottom: 24,
                                fontWeight: 700,
                                fontSize: 26,
                                color: '#222',
                                textAlign: 'center'
                            }}>
                                反馈人员
                            </h2>

                            {notifierModal.loading ? (<div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '40px 20px'
                            }}>
                                <div style={{
                                    width: 40,
                                    height: 40,
                                    border: '4px solid #f3f3f3',
                                    borderTop: '4px solid #1890ff',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginBottom: 16
                                }}></div>
                                <p style={{color: '#666', fontSize: 16}}>正在加载数据...</p>
                                <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}</style>
                            </div>) : notifierModal.data ? (<div>
                                {/* 反馈人员列表标题 */}
                                <div style={{
                                    marginBottom: 16, padding: '0 4px'
                                }}>
                                    <h3 style={{
                                        fontSize: 16, fontWeight: 600, color: '#333', margin: 0
                                    }}>
                                        反馈人员列表
                                    </h3>
                                </div>

                                {Array.isArray(notifierModal.data) && notifierModal.data.length > 0 ? (
                                    <div style={{
                                        maxHeight: '50vh',
                                        overflow: 'auto',
                                        border: '1px solid #e8eaed',
                                        borderRadius: 8,
                                        background: '#fff'
                                    }}>
                                        {notifierModal.data.map((item, index) => (
                                            <div key={item.userId || index} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '12px 16px',
                                                borderBottom: index < notifierModal.data.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                transition: 'background 0.2s ease'
                                            }}
                                                 onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                                                 onMouseLeave={(e) => e.target.style.background = '#fff'}
                                            >
                                                {/* 头像 */}
                                                <div style={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    overflow: 'hidden',
                                                    marginRight: 12,
                                                    background: '#f0f0f0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {item.avatar ? (<img
                                                        src={item.avatar}
                                                        alt={item.userName}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />) : null}
                                                    <div style={{
                                                        display: item.avatar ? 'none' : 'flex',
                                                        width: '100%',
                                                        height: '100%',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: '#e6f7ff',
                                                        color: '#1890ff',
                                                        fontSize: 16,
                                                        fontWeight: 600
                                                    }}>
                                                        {item.userName ? item.userName.charAt(0) : '?'}
                                                    </div>
                                                </div>

                                                {/* 用户名 */}
                                                <div style={{
                                                    flex: 1,
                                                    fontSize: 14,
                                                    color: '#333',
                                                    fontWeight: 500
                                                }}>
                                                    {item.userName || '未知用户'}
                                                </div>

                                                {/* 复选框 */}
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    cursor: 'pointer',
                                                    fontSize: 12,
                                                    color: '#666',
                                                    userSelect: 'none'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={notifierCheckboxes[item.userId] || false}
                                                        onChange={(e) => {
                                                            const newValue = e.target.checked;
                                                            console.log(`用户 ${item.userName} (ID: ${item.userId}) 复选框状态变化:`, newValue);
                                                            setNotifierCheckboxes(prev => ({
                                                                ...prev, [item.userId]: newValue
                                                            }));
                                                        }}
                                                        style={{
                                                            margin: 0,
                                                            width: 18,
                                                            height: 18,
                                                            accentColor: '#1890ff',
                                                            cursor: 'pointer',
                                                            border: '2px solid #d9d9d9',
                                                            borderRadius: '3px',
                                                            backgroundColor: notifierCheckboxes[item.userId] ? '#1890ff' : '#fff'
                                                        }}
                                                    />
                                                    <span style={{
                                                        color: notifierCheckboxes[item.userId] ? '#1890ff' : '#666',
                                                        fontWeight: notifierCheckboxes[item.userId] ? '500' : 'normal'
                                                    }}>
                                    接收表单提醒
                                  </span>
                                                </label>
                                            </div>))}
                                    </div>) : (<div style={{
                                    textAlign: 'center',
                                    padding: '40px 20px',
                                    color: '#666',
                                    border: '1px solid #e8eaed',
                                    borderRadius: 8,
                                    background: '#fafbfc'
                                }}>
                                    <div style={{fontSize: 48, marginBottom: 16}}>👥</div>
                                    <p style={{fontSize: 16, marginBottom: 8}}>暂无反馈人员</p>
                                    <p style={{
                                        fontSize: 14, color: '#999'
                                    }}>当前表单还没有反馈人员</p>
                                </div>)}

                                {/* 保存按钮 */}
                                <div style={{
                                    marginTop: 24, textAlign: 'center'
                                }}>
                                    <button
                                        style={{
                                            background: '#fa8c16',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '12px 32px',
                                            fontSize: 16,
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            transition: 'background 0.2s ease',
                                            boxShadow: '0 2px 8px rgba(250, 140, 22, 0.3)'
                                        }}
                                        onMouseEnter={(e) => e.target.style.background = '#e67700'}
                                        onMouseLeave={(e) => e.target.style.background = '#fa8c16'}
                                        onClick={async () => {
                                            try {
                                                // 获取勾选的用户ID
                                                const checkedUserIds = Object.keys(notifierCheckboxes)
                                                    .filter(userId => notifierCheckboxes[userId])
                                                    .join(',');

                                                console.log('保存的userIds:', checkedUserIds);

                                                const response = await saveNotifier(notifierModal.formId, checkedUserIds, false);
                                                console.log('保存响应:', response);

                                                // 检查响应格式
                                                if (response && (response.code === 200 || response.data === '保存成功' || response.message === '操作成功')) {
                                                    console.log('保存成功，关闭弹框');
                                                    console.log('设置弹框状态为关闭');
                                                    setNotifierModal({
                                                        open: false,
                                                        data: null,
                                                        loading: false,
                                                        formId: null
                                                    });
                                                    setNotifierCheckboxes({});
                                                    console.log('弹框状态已更新');
                                                } else {
                                                    console.log('保存失败:', response);
                                                    toastManager.error(response?.message || '保存失败');
                                                }
                                            } catch (error) {
                                                console.error('保存失败:', error);
                                                toastManager.error('保存失败，请重试');
                                            }
                                        }}
                                    >
                                        保存
                                    </button>
                                </div>
                            </div>) : (<div style={{
                                textAlign: 'center', padding: '40px 20px', color: '#666'
                            }}>
                                <div style={{fontSize: 48, marginBottom: 16}}>❌</div>
                                <p style={{fontSize: 16, marginBottom: 8}}>加载失败</p>
                                <p style={{fontSize: 14, color: '#999'}}>无法获取反馈提醒数据</p>
                            </div>)}
                        </div>
                    </div>)}

                    {/* 分页器 */}
                    <div style={{textAlign: 'center', margin: '32px 0'}}>
                        <button
                            disabled={page === 1}
                            onClick={() => {
                                const newPage = page - 1;
                                setPage(newPage);
                                const currentTab = showAllForms ? 'all' : 'personal';
                                navigate(`/forms?tab=${currentTab}&page=${newPage}`, { replace: true });
                            }}
                            style={{
                                marginRight: 8,
                                padding: '6px 16px',
                                borderRadius: 6,
                                border: '1px solid #d9d9d9',
                                background: page === 1 ? '#f5f5f5' : '#fff',
                                color: page === 1 ? '#bbb' : '#1890ff',
                                cursor: page === 1 ? 'not-allowed' : 'pointer',
                                fontWeight: 500,
                                transition: 'all 0.2s'
                            }}
                        >上一页
                        </button>
                        <span style={{
                            display: 'inline-block',
                            minWidth: 80,
                            fontWeight: 600,
                            color: '#222',
                            background: '#f7f7fa',
                            borderRadius: 6,
                            padding: '6px 18px',
                            margin: '0 8px',
                            border: '1px solid #e0e0e0'
                        }}>
                  第 <span style={{
                            color: '#1890ff', fontWeight: 700
                        }}>{page}</span> / {Math.max(1, Math.ceil(total / limit))} 页
                </span>
                        <button
                            disabled={page >= Math.ceil(total / limit)}
                            onClick={() => {
                                const newPage = page + 1;
                                setPage(newPage);
                                const currentTab = showAllForms ? 'all' : 'personal';
                                navigate(`/forms?tab=${currentTab}&page=${newPage}`, { replace: true });
                            }}
                            style={{
                                marginLeft: 8,
                                padding: '6px 16px',
                                borderRadius: 6,
                                border: '1px solid #d9d9d9',
                                background: page >= Math.ceil(total / limit) ? '#f5f5f5' : '#fff',
                                color: page >= Math.ceil(total / limit) ? '#bbb' : '#1890ff',
                                cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                                fontWeight: 500,
                                transition: 'all 0.2s'
                            }}
                        >下一页
                        </button>
                    </div>
                </>)}
            </div>
        </div>

        {/* 预览模态框 */}
        {previewModal.open && (
            <div style={{
                position: 'fixed',
                left: 0,
                top: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.75)',
                backdropFilter: 'blur(8px)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                animation: 'fadeIn 0.3s ease-out'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '20px',
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)',
                    animation: 'slideInUp 0.4s ease-out'
                }}>
                    {/* 模态框头部 */}
                    <div style={{
                        padding: '24px 32px',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* 背景装饰 */}
                        <div style={{
                            position: 'absolute',
                            top: '-50%',
                            right: '-20%',
                            width: '200px',
                            height: '200px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '50%',
                            transform: 'rotate(45deg)'
                        }}></div>
                        <div style={{
                            position: 'absolute',
                            bottom: '-30%',
                            left: '-10%',
                            width: '150px',
                            height: '150px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '50%',
                            transform: 'rotate(-30deg)'
                        }}></div>
                        
                        <div style={{display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1}}>
                            <div>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    letterSpacing: '-0.5px'
                                }}>
                                    表单预览
                                </h3>
                                {(previewModal.formData?.showTitle !== false) && (<p style={{
                                    margin: '4px 0 0 0',
                                    fontSize: '14px',
                                    opacity: 0.9,
                                    fontWeight: '400'
                                }}>
                                    {previewModal.formData?.title || '未命名表单'}
                                </p>)}
                            </div>
                        </div>
                        
                        <button
                            style={{
                                background: 'rgba(255,255,255,0.15)',
                                border: 'none',
                                borderRadius: '12px',
                                width: '40px',
                                height: '40px',
                                color: '#fff',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                zIndex: 1,
                                backdropFilter: 'blur(10px)'
                            }}
                            onClick={() => setPreviewModal({open: false, formData: null})}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.25)';
                                e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255,255,255,0.15)';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            ×
                        </button>
                    </div>

                    {/* 模态框内容 */}
                    <div style={{
                        padding: '0',
                        maxHeight: 'calc(80vh - 100px)',
                        overflow: 'auto',
                        background: 'linear-gradient(180deg, #fafbfc 0%, #ffffff 100%)'
                    }}>
                        {previewModal.formData && (
                            <div style={{
                                padding: '24px',
                                minHeight: '300px'
                            }}>
                                <FormFiller
                                    formId={null}
                                    formData={previewModal.formData}
                                    previewMode={true}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
    </>);
} 